import { Schema } from "mongoose"
import { z } from "zod"
import {
  SUBSCRIPTION_CHANGE_DIRECTIONS,
  SUBSCRIPTION_EVENT_TYPES,
  SUBSCRIPTION_INTERVAL_UNITS,
  SUBSCRIPTION_SCOPES,
  SUBSCRIPTION_STATUSES,
  SUBSCRIPTION_TYPES,
} from "@rpcbase/billing"


export const ZRBTenantSubscriptionStatus = z.enum(SUBSCRIPTION_STATUSES)

export const ZRBTenantSubscriptionIntervalUnit = z.enum(SUBSCRIPTION_INTERVAL_UNITS)

export const ZRBTenantSubscriptionType = z.enum(SUBSCRIPTION_TYPES)

export const ZRBTenantSubscriptionScope = z.enum(SUBSCRIPTION_SCOPES)

export const ZRBTenantSubscriptionEventType = z.enum(SUBSCRIPTION_EVENT_TYPES)


export const ZRBTenantSubscriptionEventSource = z.enum([
  "admin",
  "system",
  "webhook",
  "user",
])

export const ZRBTenantSubscriptionChangeDirection = z.enum(SUBSCRIPTION_CHANGE_DIRECTIONS)

const subscriptionEventBaseSchema = z.object({
  tenantId: z.string().trim().min(1),
  subscriptionId: z.string().trim().min(1),
  occurredAt: z.date(),
  effectiveAt: z.date(),
  subscriptionType: ZRBTenantSubscriptionType,
  parentSubscriptionId: z.string().optional(),
  scope: ZRBTenantSubscriptionScope,
  scopeId: z.string().optional(),
  fromPlanKey: z.string().optional(),
  toPlanKey: z.string().optional(),
  fromStatus: ZRBTenantSubscriptionStatus.optional(),
  toStatus: ZRBTenantSubscriptionStatus.optional(),
  fromModules: z.array(z.string()).optional(),
  toModules: z.array(z.string()).optional(),
  fromIntervalUnit: ZRBTenantSubscriptionIntervalUnit.optional(),
  toIntervalUnit: ZRBTenantSubscriptionIntervalUnit.optional(),
  fromIntervalCount: z.number().int().min(1).optional(),
  toIntervalCount: z.number().int().min(1).optional(),
  fromPriceId: z.string().optional(),
  toPriceId: z.string().optional(),
  billingAnchor: z.date().optional(),
  supersedesEventId: z.string().optional(),
  idempotencyKey: z.string().trim().min(1),
  direction: ZRBTenantSubscriptionChangeDirection.optional(),
  actorUserId: z.string().optional(),
  source: ZRBTenantSubscriptionEventSource.optional(),
  reason: z.string().optional(),
  provider: z.string().optional(),
  providerEventId: z.string().optional(),
  providerPayload: z.unknown().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
})

const subscriptionCreatedEventSchema = subscriptionEventBaseSchema.extend({
  type: z.literal("created"),
  toPlanKey: z.string().trim().min(1),
  toStatus: ZRBTenantSubscriptionStatus,
  toModules: z.array(z.string()),
  toIntervalUnit: ZRBTenantSubscriptionIntervalUnit,
  toIntervalCount: z.number().int().min(1),
  billingAnchor: z.date(),
})

const subscriptionPlanChangedEventSchema = subscriptionEventBaseSchema.extend({
  type: z.literal("plan_changed"),
  fromPlanKey: z.string().trim().min(1),
  toPlanKey: z.string().trim().min(1),
  fromModules: z.array(z.string()),
  toModules: z.array(z.string()),
  fromIntervalUnit: ZRBTenantSubscriptionIntervalUnit,
  toIntervalUnit: ZRBTenantSubscriptionIntervalUnit,
  fromIntervalCount: z.number().int().min(1),
  toIntervalCount: z.number().int().min(1),
})

const subscriptionStatusChangedEventSchema = subscriptionEventBaseSchema.extend({
  type: z.literal("status_changed"),
  fromStatus: ZRBTenantSubscriptionStatus,
  toStatus: ZRBTenantSubscriptionStatus,
})

const subscriptionResumedEventSchema = subscriptionEventBaseSchema.extend({
  type: z.literal("resumed"),
  fromStatus: ZRBTenantSubscriptionStatus,
  toStatus: z.literal("active"),
})

const subscriptionCanceledEventSchema = subscriptionEventBaseSchema.extend({
  type: z.literal("canceled"),
  fromStatus: ZRBTenantSubscriptionStatus,
  toStatus: z.literal("canceled"),
})

const subscriptionRenewedEventSchema = subscriptionEventBaseSchema.extend({
  type: z.literal("renewed"),
  fromStatus: ZRBTenantSubscriptionStatus,
  toStatus: ZRBTenantSubscriptionStatus,
})

export const ZRBTenantSubscriptionEvent = z.discriminatedUnion("type", [
  subscriptionCreatedEventSchema,
  subscriptionPlanChangedEventSchema,
  subscriptionStatusChangedEventSchema,
  subscriptionResumedEventSchema,
  subscriptionCanceledEventSchema,
  subscriptionRenewedEventSchema,
]).superRefine((event, ctx) => {
  if (event.subscriptionType === "addon" && !event.parentSubscriptionId?.trim()) {
    ctx.addIssue({
      code: "custom",
      message: "parentSubscriptionId is required for add-on subscriptions",
      path: ["parentSubscriptionId"],
    })
  }
  if (event.scope !== "tenant" && !event.scopeId?.trim()) {
    ctx.addIssue({
      code: "custom",
      message: "scopeId is required for non-tenant subscriptions",
      path: ["scopeId"],
    })
  }
})

export type IRBTenantSubscriptionEvent = z.infer<typeof ZRBTenantSubscriptionEvent>

type SubscriptionEventDocument = {
  type?: typeof SUBSCRIPTION_EVENT_TYPES[number]
  subscriptionType?: typeof SUBSCRIPTION_TYPES[number]
  scope?: typeof SUBSCRIPTION_SCOPES[number]
}

const requiredForEventTypes = (...types: Array<typeof SUBSCRIPTION_EVENT_TYPES[number]>) =>
  function requiredForEventType(this: SubscriptionEventDocument): boolean {
    return Boolean(this.type && types.includes(this.type))
  }

export const RBTenantSubscriptionEventSchema: Schema = new Schema(
  {
    tenantId: { type: String, required: true, index: true },
    subscriptionId: { type: String, required: true, index: true },
    type: { type: String, required: true, enum: ZRBTenantSubscriptionEventType.options },
    occurredAt: { type: Date, required: true, default: Date.now },
    effectiveAt: { type: Date, required: true },
    subscriptionType: { type: String, required: true, enum: ZRBTenantSubscriptionType.options },
    parentSubscriptionId: {
      type: String,
      required(this: SubscriptionEventDocument) {
        return this.subscriptionType === "addon"
      },
    },
    scope: { type: String, required: true, enum: ZRBTenantSubscriptionScope.options },
    scopeId: {
      type: String,
      required(this: SubscriptionEventDocument) {
        return Boolean(this.scope && this.scope !== "tenant")
      },
    },
    fromPlanKey: { type: String, required: requiredForEventTypes("plan_changed") },
    toPlanKey: { type: String, required: requiredForEventTypes("created", "plan_changed") },
    fromStatus: {
      type: String,
      required: requiredForEventTypes("status_changed", "resumed", "canceled", "renewed"),
      enum: ZRBTenantSubscriptionStatus.options,
    },
    toStatus: {
      type: String,
      required: requiredForEventTypes("created", "status_changed", "resumed", "canceled", "renewed"),
      enum: ZRBTenantSubscriptionStatus.options,
    },
    fromModules: { type: [String], required: requiredForEventTypes("plan_changed"), default: undefined },
    toModules: { type: [String], required: requiredForEventTypes("created", "plan_changed"), default: undefined },
    fromIntervalUnit: {
      type: String,
      required: requiredForEventTypes("plan_changed"),
      enum: ZRBTenantSubscriptionIntervalUnit.options,
    },
    toIntervalUnit: {
      type: String,
      required: requiredForEventTypes("created", "plan_changed"),
      enum: ZRBTenantSubscriptionIntervalUnit.options,
    },
    fromIntervalCount: { type: Number, required: requiredForEventTypes("plan_changed"), min: 1 },
    toIntervalCount: { type: Number, required: requiredForEventTypes("created", "plan_changed"), min: 1 },
    fromPriceId: { type: String },
    toPriceId: { type: String },
    billingAnchor: { type: Date, required: requiredForEventTypes("created") },
    supersedesEventId: { type: String },
    idempotencyKey: { type: String, required: true },
    direction: { type: String, enum: ZRBTenantSubscriptionChangeDirection.options },
    actorUserId: { type: String },
    source: { type: String, enum: ZRBTenantSubscriptionEventSource.options },
    reason: { type: String },
    provider: { type: String },
    providerEventId: { type: String },
    providerPayload: { type: Schema.Types.Mixed },
    metadata: { type: Schema.Types.Mixed },
  },
)

RBTenantSubscriptionEventSchema.index({ tenantId: 1, subscriptionId: 1, effectiveAt: 1, occurredAt: 1 })
RBTenantSubscriptionEventSchema.index(
  { provider: 1, providerEventId: 1 },
  {
    unique: true,
    partialFilterExpression: {
      provider: { $type: "string" },
      providerEventId: { $type: "string" },
    },
  },
)
RBTenantSubscriptionEventSchema.index({ tenantId: 1, idempotencyKey: 1 }, { unique: true })
